import * as React from "react"

import { cn } from "@/lib/utils"
import { formatPriceParts, type BillingCycle, type Currency } from "@/lib/format"

/**
 * Price typography.
 *
 * The amount is rendered LTR-isolated (`.ltr-nums`) while the currency and the
 * per-cycle suffix stay in the RTL flow — that's the combination that reads
 * naturally in Persian without the digits jumping around the unit.
 */
export function Price({
  amount,
  currency = "IRT",
  cycle,
  size = "md",
  tone = "default",
  className,
}: {
  amount: number
  currency?: Currency
  cycle?: BillingCycle | null
  size?: "sm" | "md" | "lg" | "xl"
  tone?: "default" | "success"
  className?: string
}) {
  const { amount: value, unit } = formatPriceParts(amount, currency)

  const amountSize = {
    sm: "text-xl",
    md: "text-3xl",
    lg: "text-4xl",
    xl: "text-5xl",
  }[size]

  const unitSize = {
    sm: "text-[0.6875rem]",
    md: "text-xs",
    lg: "text-sm",
    xl: "text-base",
  }[size]

  return (
    <p className={cn("flex flex-wrap items-baseline gap-x-1.5", className)}>
      <span
        className={cn(
          amountSize,
          "ltr-nums font-extrabold tracking-[-0.015em] transition-colors duration-300",
          tone === "success"
            ? "text-success-600 dark:text-success-400"
            : "text-[var(--content)]",
        )}
      >
        {value}
      </span>
      <span className={cn(unitSize, "font-semibold text-[var(--content-muted)]")}>{unit}</span>
      {cycle ? (
        <span className={cn(unitSize, "font-medium text-[var(--content-subtle)]")}>
          / {CYCLE_LABEL[cycle]}
        </span>
      ) : null}
    </p>
  )
}

const CYCLE_LABEL: Record<BillingCycle, string> = {
  monthly: "ماه",
  annually: "سال",
  biennially: "دو سال",
  triennially: "سه سال",
}

/** Struck-through original price, shown beside a discounted one. */
export function PriceStrike({
  amount,
  currency = "IRT",
  className,
}: {
  amount: number
  currency?: Currency
  className?: string
}) {
  const { amount: value } = formatPriceParts(amount, currency)
  return (
    <span
      className={cn(
        "ltr-nums text-sm font-medium text-[var(--content-subtle)] line-through decoration-danger-500/60",
        className,
      )}
    >
      {value}
    </span>
  )
}
